import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { STATUS_ORDER, STATUS_LABEL, relTime, slaCountdown } from "../lib/labels";
import { PriorityBadge, CategoryBadge } from "../components/Badges";
import { Clock, Ticket, Note, User, PhoneCall } from "@phosphor-icons/react";
import { toast } from "sonner";
import PostItBoard from "../components/PostItBoard";
import CallsBoard from "../components/CallsBoard";
import { openTicketFromEvent } from "../lib/openTicket";

const VIEWS = [
  { key: "tickets",  label: "Tickets",   icon: Ticket,    testid: "kanban-view-tickets", dotBg: "bg-blue-500" },
  { key: "common",   label: "Commun",    icon: Note,      testid: "kanban-view-common",  dotBg: "bg-amber-500" },
  { key: "personal", label: "Personnel", icon: User,      testid: "kanban-view-personal", dotBg: "bg-violet-500" },
  { key: "calls",    label: "Appels IA", icon: PhoneCall, testid: "kanban-view-calls",   dotBg: "bg-emerald-500" },
];

function TicketKanban() {
  const [tickets, setTickets] = useState([]);
  const [closedTotal, setClosedTotal] = useState(0);
  const CLOSED_PREVIEW = 15; // most recent closed tickets shown in the Fermé column
  const dragId = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    // Sync with the "Masquer appels IA" toggle from /tickets so both views agree.
    let hideAi = false;
    try { hideAi = window.localStorage.getItem("mt.tickets.hideAi") === "true"; } catch { /* noop */ }
    // Two focused requests → scales to thousands of closed tickets:
    //  1) All OPEN tickets (nouveau / en_attente / en_cours) — cap 500
    //  2) Only the CLOSED_PREVIEW most-recently-updated CLOSED tickets, + total count
    const openParams = { limit: 500, hide_closed: "true" };
    const closedParams = { limit: CLOSED_PREVIEW, status: "ferme", sort: "updated_desc" };
    if (hideAi) { openParams.hide_ai = "true"; closedParams.hide_ai = "true"; }
    const [openR, closedR] = await Promise.all([
      api.get("/tickets", { params: openParams }),
      api.get("/tickets", { params: closedParams }),
    ]);
    const openList = openR.data?.items || (Array.isArray(openR.data) ? openR.data : []);
    const closedList = closedR.data?.items || (Array.isArray(closedR.data) ? closedR.data : []);
    setTickets([...openList, ...closedList]);
    setClosedTotal(closedR.data?.total || closedList.length);
  };
  useEffect(() => { load(); }, []);

  // Custom column order requested by users: nouveau → en_attente → en_cours → ferme
  // (No "resolu" column — technicians close via "ferme" directly on drag-drop.)
  const KANBAN_COLS = ["nouveau", "en_attente", "en_cours", "ferme"];
  const cols = KANBAN_COLS.map((s) => ({
    key: s, label: STATUS_LABEL[s],
    tickets: tickets.filter((t) => t.status === s),
  }));

  const onDrop = async (status) => {
    const id = dragId.current;
    if (!id) return;
    try {
      await api.patch(`/tickets/${id}`, { status });
      toast.success(`Ticket déplacé → ${STATUS_LABEL[status]}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {cols.map((col) => (
        <div
          key={col.key}
          className="kanban-col shrink-0 rounded-lg bg-muted/30 border"
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => onDrop(col.key)}
          data-testid={`kanban-col-${col.key}`}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="flex items-center gap-2">
              <span className={`status-dot status-${col.key}`}/>
              <div className="font-display font-semibold text-sm">{col.label}</div>
            </div>
            <span className="text-xs mono text-muted-foreground">
              {col.key === "ferme" ? closedTotal : col.tickets.length}
            </span>
          </div>
          <div className="p-3 space-y-3 min-h-[200px]">
            {col.tickets.map((t) => {
              const sla = slaCountdown(t.sla_resolution_deadline);
              return (
                <Card key={t.id}
                  draggable
                  onDragStart={() => (dragId.current = t.id)}
                  onClick={(e) => openTicketFromEvent(e, `/tickets/${t.id}`, nav)}
                  onAuxClick={(e) => openTicketFromEvent(e, `/tickets/${t.id}`, nav)}
                  className="cursor-pointer card-hover"
                  data-testid={`kanban-card-${t.number}`}>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <Link
                        to={`/tickets/${t.id}`}
                        data-testid={`kanban-card-link-${t.number}`}
                        onClick={(e) => e.stopPropagation()}
                        className="mono text-muted-foreground hover:underline"
                      >
                        #{t.number}
                      </Link>
                      <PriorityBadge value={t.priority}/>
                    </div>
                    <div className="font-medium text-sm leading-snug line-clamp-2">{t.subject}</div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <CategoryBadge value={t.category}/>
                      <span className={`mono ${sla.breached ? "text-destructive font-semibold" : sla.urgent ? "text-orange-500" : ""}`}>
                        <Clock size={11} className="inline mr-1"/>{sla.text}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {t.client_name} · {t.assignee_name || "non assigné"} · {relTime(t.created_at)}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {col.tickets.length === 0 && (
              <div className="text-xs text-muted-foreground text-center py-6">Aucun ticket</div>
            )}
            {col.key === "ferme" && closedTotal > col.tickets.length && (
              <Link
                to="/tickets?status=ferme"
                data-testid="kanban-see-all-closed"
                className="block text-center text-xs text-primary hover:underline pt-1 pb-2"
              >
                Voir les {closedTotal} tickets fermés →
              </Link>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Kanban() {
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("kanban_view") || "tickets"; } catch { return "tickets"; }
  });
  const [counts, setCounts] = useState({ tickets: 0, common: 0, personal: 0, calls: 0 });
  const switchView = (v) => {
    setView(v);
    try { localStorage.setItem("kanban_view", v); } catch { /* noop */ }
  };

  useEffect(() => {
    let cancelled = false;
    const loadCounts = async () => {
      try {
        // Match the /tickets page filters (hideClosed default true, hideAi per user)
        let hideAi = false;
        try { hideAi = window.localStorage.getItem("mt.tickets.hideAi") === "true"; } catch { /* noop */ }
        const tParams = { per_page: 1, hide_closed: "true" };
        if (hideAi) tParams.hide_ai = "true";
        const [tR, cR, pR, aR] = await Promise.all([
          api.get("/tickets", { params: tParams }),
          api.get("/postits", { params: { scope: "common" } }),
          api.get("/postits", { params: { scope: "personal" } }),
          api.get("/tickets", { params: { per_page: 1, ai: "only", hide_closed: "true" } }),
        ]);
        if (cancelled) return;
        setCounts({
          tickets: Number(tR.data?.total ?? (Array.isArray(tR.data) ? tR.data.length : 0)),
          common: (cR.data || []).length,
          personal: (pR.data || []).length,
          calls: Number(aR.data?.total ?? 0),
        });
      } catch { /* ignore */ }
    };
    loadCounts();
    const t = setInterval(loadCounts, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [view]);

  const current = VIEWS.find((v) => v.key === view) || VIEWS[0];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps">Flux de travail</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Kanban</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {view === "tickets" && "Glissez-déposez pour changer le statut d'un ticket."}
            {view === "common" && "Post-its partagés — visibles par toute l'équipe support."}
            {view === "personal" && "Vos post-its personnels — visibles uniquement par vous."}
            {view === "calls" && "Appels traités par l'assistant vocal IA."}
          </p>
        </div>

        <div className="inline-flex bg-muted/40 border rounded-lg p-1 gap-1" role="tablist" data-testid="kanban-view-switcher">
          {VIEWS.map((v) => {
            const Icon = v.icon;
            const active = view === v.key;
            const n = counts[v.key] || 0;
            return (
              <button key={v.key}
                onClick={() => switchView(v.key)}
                data-testid={v.testid}
                role="tab"
                aria-selected={active}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  active
                    ? "bg-background shadow-sm font-semibold"
                    : "text-muted-foreground hover:text-foreground hover:bg-background/50"
                }`}>
                <Icon size={15} weight={active ? "fill" : "regular"}/>
                <span className="hidden sm:inline">{v.label}</span>
                <span
                  data-testid={`${v.testid}-count`}
                  className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold text-white ring-1 ring-black/5 tabular-nums ${v.dotBg} ${n === 0 ? "opacity-40" : ""}`}
                  title={`${n} élément${n > 1 ? "s" : ""}`}>
                  {n > 99 ? "99+" : n}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {view === "tickets" && <TicketKanban/>}
      {view === "common" && <PostItBoard scope="common"/>}
      {view === "personal" && <PostItBoard scope="personal"/>}
      {view === "calls" && <CallsBoard/>}
    </div>
  );
}
